<template>
  <!-- 银行到账核对 — 국민은행 거래내역을 올리면 입금 행마다 구매 청구 후보를 보여 주고,
       직원이 확인한 것만 기존 입금확인(입금여부·입금일시)으로 반영한다(YZY 2026-10-07).
       매칭 규칙은 서버 inventory-services/bank-reconcile.js -->
  <b-modal :visible="value" @change="v => $emit('input', v)" size="xl" hide-footer dialog-class="bank-reconcile-dialog"
           title="🏦 银行到账核对 (은행 입금내역 대조)" body-class="pt-2">
    <div class="d-flex flex-wrap align-items-center mb-2">
      <label class="btn btn-sm btn-outline-primary mb-0 mr-2">
        ⬆ 上传银行流水(국민은행 거래내역 .xls 올리기)
        <input type="file" accept=".xls,.xlsx" class="d-none" @change="onFile">
      </label>
      <span v-if="fileName" class="small text-muted mr-3">{{ fileName }} · 入账 {{ rows.length }} 行(입금 {{ rows.length }}건) · 出账忽略 {{ skipped }} 行(출금 무시)</span>
      <b-spinner v-if="loading" small class="mr-2"></b-spinner>
      <b-button size="sm" variant="outline-secondary" class="mr-2" @click="aliasOpen = true">📇 汇款人别名表(입금자 별칭 표)</b-button>
      <div class="ml-auto" v-if="rows.length">
        <b-button size="sm" variant="outline-secondary" class="mr-2" @click="exportXlsx">导出(엑셀)</b-button>
        <b-button size="sm" variant="success" :disabled="!selectedCount || applying" @click="applySelected">
          确认到账 (입금확인 반영) {{ selectedCount }}笔/건
        </b-button>
      </div>
    </div>
    <p class="small text-muted mb-2">
      只核对采购账单(구매 청구만 대조): 金额=报价单金额或 1688金额(CNY)×汇率×1.1. 出库账单(배송 청구)·关税·销毁·检测费 系统里还没有账单 → 未匹配, 只选分类(분류만 선택·엑셀로 내보내기).
      确定=货主名+金额一致(이름·금액 일치) · 需确认=只有金额一致等(금액만 일치 등 — 직접 확인 후 체크) · 未匹配=找不到(못 찾음) · 已确认=之前已手动确认到账(이미 뱃지로 입금확인한 청구와 일치 — 할 일 없음).
    </p>
    <div v-if="error" class="alert alert-danger py-1 small">{{ error }}</div>

    <div v-if="rows.length" class="mb-2">
      <b-button-group size="sm">
        <b-button v-for="f in filters" :key="f.k" :variant="filter === f.k ? 'primary' : 'outline-primary'" @click="filter = f.k">
          {{ f.label }} <b-badge variant="light">{{ f.k === 'all' ? rows.length : (counts[f.k] || 0) }}</b-badge>
        </b-button>
      </b-button-group>
    </div>

    <div v-if="rows.length" class="table-responsive" style="max-height: 62vh;">
      <!-- 사유(说明)가 밀지 않고 보이게: 넓은 화면은 표가 창 폭 안에 들게(칸 최소폭 줄임·사유 줄바꿈),
           좁은 화면(<1200px · 폰·태블릿·작은 노트북)은 bootstrap-vue 쌓기 표(b-table-stacked-xl · data-label 머리)로 칸을 세로로 -->
      <table class="table b-table b-table-stacked-xl table-sm table-bordered small mb-0 bank-reconcile-table">
        <thead class="thead-light" style="position: sticky; top: 0; z-index: 1;">
          <tr>
            <th style="width: 32px;"></th>
            <th>交易时间<br><small>(거래일시)</small></th>
            <th style="min-width: 100px;">汇款人<br><small>(보낸분)</small></th>
            <th class="text-right">入账金额<br><small>(입금액)</small></th>
            <th>结果<br><small>(판정)</small></th>
            <th>货主<br><small>(화주)</small></th>
            <th style="min-width: 160px;">匹配账单<br><small>(매칭 청구 — 묶음번호·금액)</small></th>
            <th style="min-width: 140px;">分类<br><small>(분류)</small></th>
            <th style="min-width: 140px;">说明<br><small>(사유)</small></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in visibleRows" :key="r.key" :class="rowClass(r)">
            <td class="text-center" data-label="">
              <div>
                <input v-if="canApply(r)" type="checkbox" v-model="selected[r.key]">
                <b-button v-else-if="r.판정 === '已反映'" size="sm" variant="outline-danger" class="py-0 px-1" :disabled="applying" @click="undoRow(r)">取消<br><small>(취소)</small></b-button>
              </div>
            </td>
            <td data-label="交易时间(거래일시)"><div>{{ r.거래일시 }}</div></td>
            <td data-label="汇款人(보낸분)"><div>{{ r.보낸분 }}</div></td>
            <td class="text-right text-nowrap" data-label="入账金额(입금액)"><div>₩{{ won(r.입금액) }}</div></td>
            <td data-label="结果(판정)"><div><b-badge :variant="badge(r.판정)">{{ label(r.판정) }}</b-badge></div></td>
            <td data-label="货主(화주)">
              <div>
                <div v-if="r.companyid">{{ r.companyid }}<br><small class="text-muted">{{ names[r.companyid] }}</small></div>
                <div v-if="bizLine(r)" class="text-muted"><small>{{ bizLine(r) }}</small></div>
                <!-- 동명이인(같은 입금자명 → 다른 회사 2+) — 후보를 직접 고른다(판 #876 ②) -->
                <div v-if="r.동명이인 && r.후보상세 && r.후보상세.length" class="mt-1">
                  <b-badge variant="danger" class="mb-1">同名 {{ r.동명수 || r.후보상세.length }}</b-badge>
                  <select class="form-control form-control-sm bank-reconcile-pick" :value="r.companyid" :disabled="r.판정 === '已反映' || applying" @change="pickCandidate(r, $event.target.value)">
                    <option value="" disabled>-- 选择货主(화주 선택) --</option>
                    <option v-for="c in r.후보상세" :key="c.companyid" :value="c.companyid">{{ names[c.companyid] || c.companyid }}{{ c.bizLast4 ? ' · ****' + c.bizLast4 : '' }}</option>
                  </select>
                </div>
                <div v-else-if="r.회사후보 && r.회사후보.length > 1" class="text-muted"><small>候选: {{ r.회사후보.join(', ') }}</small></div>
              </div>
            </td>
            <td data-label="匹配账单(매칭 청구)">
              <div>
                <div v-for="b in r.청구" :key="b.key">
                  {{ batchNo(b.요청일) }} · {{ zh(b.reqtype) || '-' }} · {{ zh(b.통관방법) }} · <b>₩{{ won(b.금액) }}</b>
                  <small class="text-muted">({{ b.ids.length }}件 · {{ b.상태.map(zh).join('/') }})</small>
                </div>
                <div v-if="r.청구 && r.청구.length > 1" class="text-nowrap"><small>合计(합계) ₩{{ won(sumBills(r.청구)) }}</small></div>
                <div v-if="!r.청구.length && r.참고청구" class="text-muted">
                  <small>参考(참고·금액 다름): {{ batchNo(r.참고청구.요청일) }} ₩{{ won(r.참고청구.금액) }}</small>
                </div>
                <div v-if="r.판정 === '已反映'" class="text-muted"><small>已反映 {{ r.반영ids.length }}件(반영됨)</small></div>
              </div>
            </td>
            <td data-label="分类(분류)">
              <div>
                <select v-model="r.분류" class="form-control form-control-sm" :disabled="r.판정 === '已反映'">
                  <option value="">-- 选择(선택) --</option>
                  <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
                </select>
              </div>
            </td>
            <td class="bank-reconcile-reason" data-label="说明(사유)"><div><small>{{ r.사유 }}</small></div></td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- 별칭 표를 고친 뒤 닫으면 대조를 다시 돌려 후보를 새로 본다 -->
    <DepositorAliasModal v-model="aliasOpen" />
  </b-modal>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';
import { parseBankStatement } from './bankStatementParser';
import { PROCESS_STATUS_TEXT } from '@/utils/coupassStatusText';
import DepositorAliasModal from './DepositorAliasModal';

// 저장값 → 「中文 (한국어)」 (商品求购 displayZh 와 같은 규칙, 2026-10 YZY)
const ZH = { ...PROCESS_STATUS_TEXT, 통합통관: '综合清关', 화주단독통관: '货主单独清关', 쿠패스단독통관: 'COUPASS单独清关', 제트배송: 'Growth', 로켓배송: '火箭', 일반LCL: '普通LCL', 샘플검증: '样品验证' };

const LABEL = { 确定: '确定(확정)', 需确认: '需确认(확인필요)', 未匹配: '未匹配(미매칭)', 已反映: '已反映(반영됨)', 已确认: '已确认(이미 수동 입금확인)' };
const BADGE = { 确定: 'success', 需确认: 'warning', 未匹配: 'secondary', 已反映: 'info', 已确认: 'light' };

export default {
  name: 'BankReconcileModal',
  components: { DepositorAliasModal },
  props: { value: { type: Boolean, default: false } },
  data() {
    return {
      aliasOpen: false,
      fileName: '',
      inputRows: [],
      skipped: 0,
      rows: [],
      counts: {},
      names: {},
      categories: [],
      selected: {},
      picks: {},
      filter: 'all',
      loading: false,
      applying: false,
      error: ''
    };
  },
  watch: {
    aliasOpen(open) { if (!open && this.inputRows.length) this.preview(false); }
  },
  computed: {
    filters() {
      return [
        { k: 'all', label: '全部(전체)' },
        { k: '确定', label: '确定(확정)' },
        { k: '需确认', label: '需确认(확인필요)' },
        { k: '未匹配', label: '未匹配(미매칭)' },
        { k: '已反映', label: '已反映(반영됨)' },
        { k: '已确认', label: '已确认(이미 수동 확인)' }
      ];
    },
    visibleRows() {
      return this.filter === 'all' ? this.rows : this.rows.filter(r => r.판정 === this.filter);
    },
    selectedCount() {
      return this.rows.filter(r => this.canApply(r) && this.selected[r.key]).length;
    }
  },
  methods: {
    won(n) { return Math.round(Number(n) || 0).toLocaleString(); },
    zh(v) { return ZH[v] ? ZH[v] + ' (' + v + ')' : (v || ''); },
    label(s) { return LABEL[s] || s; },
    badge(s) { return BADGE[s] || 'secondary'; },
    rowClass(r) { return r.판정 === '确定' ? 'table-success' : r.판정 === '需确认' ? 'table-warning' : ''; },
    canApply(r) { return (r.판정 === '确定' || r.판정 === '需确认') && r.청구 && r.청구.length > 0; },
    sumBills(bills) { return bills.reduce((s, b) => s + (b.금액 || 0), 0); },
    bizLine(r) {
      const parts = [];
      if (r.bizLast4) parts.push('营业执照 ****' + r.bizLast4);
      if (r.근거) parts.push('依据: ' + r.근거);
      return parts.join(' · ');
    },
    // 동명이인 후보 고르기 — 그 행 화주·청구 = 서버가 미리 계산한 그 후보 값. 다시 확인 체크하게 체크는 끈다
    applyPick(r, cid) {
      const c = (r.후보상세 || []).find(x => x.companyid === cid);
      if (!c) return false;
      r.companyid = c.companyid;
      r.청구 = c.청구 || [];
      r.bizLast4 = c.bizLast4 || '';
      r.근거 = c.via || '';
      // 사유도 고른 후보 기준으로 — 옛 사유(첫 후보 기준)가 남아 헷갈리지 않게
      r.사유 = `同名${r.동명수 || r.후보상세.length} · 已选 (직접 고름) · ` + (r.청구.length ? '金额一致 (금액 일치)' : '无金额一致账单 (금액 맞는 청구 없음)');
      return true;
    },
    pickCandidate(r, cid) {
      if (!this.applyPick(r, cid)) return;
      this.$set(this.picks, r.key, cid);
      this.$set(this.selected, r.key, false);
    },
    // 商品求购 화면의 묶음번호와 같은 모양(KST yyMMdd-HHmmss)
    batchNo(t) {
      const d = new Date(Number(t));
      if (isNaN(d.getTime())) return '';
      const k = new Date(d.getTime() + 9 * 3600 * 1000).toISOString();
      return k.slice(2, 4) + k.slice(5, 7) + k.slice(8, 10) + '-' + k.slice(11, 13) + k.slice(14, 16) + k.slice(17, 19);
    },
    async onFile(ev) {
      const file = ev.target.files && ev.target.files[0];
      ev.target.value = '';
      if (!file) return;
      this.error = '';
      this.fileName = file.name;
      try {
        const buf = await file.arrayBuffer();
        const parsed = parseBankStatement(buf);
        this.inputRows = parsed.rows;
        this.skipped = parsed.출금행;
        this.picks = {};
        if (!parsed.rows.length) { this.rows = []; this.error = '入账行为0(입금 행이 없습니다)'; return; }
        await this.preview(true);
      } catch (e) {
        console.error('[银行到账核对] 파일 읽기 실패:', e);
        this.error = '文件读取失败(파일 읽기 실패): ' + (e.message || e);
      }
    },
    // 서버 대조. resetSelection=true 면 确定 만 기본 체크
    async preview(resetSelection) {
      this.loading = true;
      try {
        const res = await axios.post('/api/coupass/bankReconcile/preview', { rows: this.inputRows });
        if (res.data.요청결과 !== '성공') throw new Error((res.data.error && res.data.error.errorMessage) || '대조 실패');
        const prevCat = {};
        this.rows.forEach(r => { if (r.분류) prevCat[r.key] = r.분류; });
        this.rows = res.data.rows.map(r => ({ ...r, 분류: prevCat[r.key] || r.분류 || '' }));
        // 직원이 고른 동명이인 후보는 다시 대조해도 그대로
        this.rows.forEach(r => { if (r.동명이인 && this.picks[r.key]) this.applyPick(r, this.picks[r.key]); });
        this.counts = res.data.counts || {};
        this.names = res.data.names || {};
        this.categories = res.data.categories || [];
        const sel = {};
        this.rows.forEach(r => {
          if (!this.canApply(r)) return;
          sel[r.key] = resetSelection ? r.판정 === '确定' : !!this.selected[r.key];
        });
        this.selected = sel;
        return this.rows;
      } catch (e) {
        console.error('[银行到账核对] 대조 실패:', e);
        this.error = '核对失败(대조 실패): ' + (e.message || e);
        return null;
      } finally {
        this.loading = false;
      }
    },
    // 기존 입금확인 경로 그대로 — 商品求购 「입금확인」 뱃지(togglePaymentStatus)가 쓰는 엔드포인트·필드
    // done(Set) 을 주면 성공한 묶음의 _id 를 담는다 — 별칭 저장이 성공한 행만 고르려고
    async saveItems(items, done) {
      for (let i = 0; i < items.length; i += 200) {
        const chunk = items.slice(i, i + 200);
        const res = await axios.post('/api/reqpurchase/updatereqpurchase', chunk);
        if (res.data.요청결과 !== '변경완료') throw new Error(res.data.요청결과 || '저장 실패');
        if (done) chunk.forEach(it => done.add(it._id));
      }
    },
    // 반영된 행의 입금자명 → 입금자 별칭 표(덤 — 실패해도 반영은 그대로). 결과 알림 글을 돌려준다
    async saveAliases(rows) {
      const items = rows.filter(r => r.보낸분 && r.companyid).map(r => ({ depositor: r.보낸분, companyid: r.companyid, key: r.key }));
      if (!items.length) return '';
      let saved = 0, existed = 0, skipped = 0;
      try {
        for (let i = 0; i < items.length; i += 200) {
          const res = await axios.post('/api/coupass/depositorAlias/fromApply', { items: items.slice(i, i + 200) });
          if (res.data.요청결과 !== '성공') throw new Error((res.data.error && res.data.error.errorMessage) || '별칭 저장 실패');
          saved += res.data.saved || 0; existed += res.data.existed || 0; skipped += (res.data.skipped || []).length;
        }
      } catch (e) {
        console.error('[银行到账核对] 별칭 저장 실패:', e);
        return `别名保存失败 (별칭 저장 실패): ${e.message || e} · 已存 (저장) ${saved}`;
      }
      return `已存别名 (별칭 저장) ${saved} · 已有 (이미 있음) ${existed} · 跳过 (건넘) ${skipped}`;
    },
    async applySelected() {
      const picked = this.rows.filter(r => this.canApply(r) && this.selected[r.key]);
      if (!picked.length) return;
      // 같은 청구를 두 은행 행이 가리키면 막는다
      const seen = {};
      for (const r of picked) {
        for (const b of r.청구) {
          for (const id of b.ids) {
            if (seen[id] && seen[id] !== r.key) {
              alert('两笔入账指向同一账单，请只勾选一笔 (두 입금이 같은 청구를 가리킵니다 — 하나만 체크하세요)');
              return;
            }
            seen[id] = r.key;
          }
        }
      }
      if (!confirm(`确认到账 ${picked.length} 笔，继续吗？
${picked.length}건 입금을 입금확인으로 반영합니다. 계속할까요?`)) return;
      this.applying = true;
      try {
        // 반영 직전에 다시 대조 — 다른 직원이 방금 반영한 행은 건너뛴다
        const keys = new Set(picked.map(r => r.key));
        const cats = {}, billKeys = {};
        picked.forEach(r => { cats[r.key] = r.분류; billKeys[r.key] = r.청구.map(b => b.key).sort().join(','); });
        const fresh = await this.preview(false);
        if (!fresh) return;
        const now = new Date().toISOString();
        const user = this.$store.state.user || {};
        const items = [];
        const appliedRows = [];
        let skipped = 0;
        fresh.filter(r => keys.has(r.key)).forEach(r => {
          // 직원이 본 청구와 지금 청구가 다르면(그새 다른 반영이 있었다) 반영하지 않는다
          if (!this.canApply(r) || r.청구.map(b => b.key).sort().join(',') !== billKeys[r.key]) { skipped++; return; }
          appliedRows.push({ key: r.key, 보낸분: r.보낸분, companyid: r.companyid, ids: [].concat(...r.청구.map(b => b.ids)) });
          const 분류 = cats[r.key] || r.분류 || '';
          r.청구.forEach(b => b.ids.forEach(id => items.push({
            _id: id,
            입금여부: true,
            입금일시: now,
            입금은행내역: {
              key: r.key, 거래일시: r.거래일시, 입금자: r.보낸분, 입금액: r.입금액, 분류,
              반영자: user.email || user.companyid || '', 반영일시: now
            }
          })));
        });
        const done = new Set();
        let saveErr = null;
        if (items.length) { try { await this.saveItems(items, done); } catch (e) { saveErr = e; } }
        // updatereqpurchase 가 성공한 행만 별칭 저장 — 반영 실패가 있어도 성공분은 저장
        const aliasMsg = await this.saveAliases(appliedRows.filter(r => r.ids.length && r.ids.every(id => done.has(id))));
        if (aliasMsg) this.$bvToast.toast(aliasMsg, { title: '汇款人别名表 (입금자 별칭 표)', variant: 'info', solid: true });
        if (saveErr) throw saveErr;
        await this.preview(false);
        this.$bvToast.toast(`已确认到账 (반영) ${keys.size - skipped}笔/건` + (skipped ? ` · 已被处理跳过 (이미 처리돼 건너뜀) ${skipped}笔/건` : ''), { title: '银行到账核对', variant: 'success', solid: true });
        this.$emit('applied');
      } catch (e) {
        console.error('[银行到账核对] 반영 실패:', e);
        this.error = '反映失败(반영 실패): ' + (e.message || e);
      } finally {
        this.applying = false;
      }
    },
    async undoRow(r) {
      if (!r.반영ids || !r.반영ids.length) return;
      if (!confirm(`取消这笔入账 (${r.거래일시} ₩${this.won(r.입금액)}) 的确认，${r.반영ids.length} 件改回未入账，继续吗？
이 입금의 반영을 취소하고 ${r.반영ids.length}건을 미입금으로 되돌립니다. 계속할까요?`)) return;
      this.applying = true;
      try {
        await this.saveItems(r.반영ids.map(id => ({ _id: id, 입금여부: false, 입금일시: null, 입금은행내역: null })));
        await this.preview(false);
        this.$emit('applied');
      } catch (e) {
        console.error('[银行到账核对] 취소 실패:', e);
        this.error = '取消失败(취소 실패): ' + (e.message || e);
      } finally {
        this.applying = false;
      }
    },
    exportXlsx() {
      const head = ['交易时间(거래일시)', '汇款人(보낸분)', '入账金额(입금액)', '结果(판정)', '货主(화주)', '分类(분류)', '匹配账单(묶음번호)', '账单金额(청구금액)', '说明(사유)'];
      const body = this.rows.map(r => [
        r.거래일시, r.보낸분, r.입금액, this.label(r.판정), r.companyid || '', r.분류 || '',
        r.청구.map(b => this.batchNo(b.요청일)).join(', '), r.청구.length ? this.sumBills(r.청구) : '', r.사유 || ''
      ]);
      const ws = XLSX.utils.aoa_to_sheet([head, ...body]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '到账核对');
      const d = new Date();
      const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
      XLSX.writeFile(wb, `银行到账核对_${stamp}.xlsx`);
    }
  }
};
</script>

<style>
/* 칸이 많아 xl(1140px)로는 좁다 — 화면 폭에 맞춘다 */
.bank-reconcile-dialog { max-width: 96vw; }
/* 사유 글은 칸 안에서 줄을 바꾼다(가로로 밀지 않게) */
.bank-reconcile-table .bank-reconcile-reason { white-space: normal; overflow-wrap: anywhere; }
/* 동명이인 화주 고르기 — 넓은 표에선 «이름 · ****끝4» 가 다 보이게 최소 폭(표 칸 폭은 내용으로 정해져 % 는 안 먹음) */
.bank-reconcile-table .bank-reconcile-pick { min-width: 11rem; }
/* 좁은 화면 쌓기 표: 행 사이 굵은 금 · 빈 머리(체크 칸)는 숨김 · 고르기 칸은 칸 폭 안으로 */
@media (max-width: 1199.98px) {
  .bank-reconcile-table .bank-reconcile-pick { min-width: 0; }
  .bank-reconcile-table > tbody > tr + tr { border-top: 3px solid #dee2e6; }
  .bank-reconcile-table > tbody > tr > td[data-label=""]::before { display: none; }
}
</style>
